import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { motion, useAnimation } from "framer-motion";
import {
  FiTarget,
  FiDollarSign,
  FiTrendingUp,
  FiCalendar,
  FiPieChart,
  FiAward,
  FiCheckCircle,
  FiChevronRight,
  FiBarChart2,
  FiClock,
  FiPercent,
  FiBell,
  FiDownload,
  FiShare2,
  FiRefreshCw,
  FiStar,
} from "react-icons/fi";

const GoalShawcase = () => {
  const [activeGoal, setActiveGoal] = useState(0);
  const [goalType, setGoalType] = useState("financial");
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const progressControls = useAnimation();
  const financeProgressControls = useAnimation();

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 430);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const goalTypes = [
    {
      id: "financial",
      name: "Financial",
      shortName: "Finance",
      icon: <FiDollarSign />,
      color: "#10B981",
      gradient: "linear-gradient(135deg, #10B981, #059669)",
    },
    {
      id: "fitness",
      name: "Fitness",
      shortName: "Fitness",
      icon: <FiTrendingUp />,
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #2563EB)",
    },
    {
      id: "learning",
      name: "Learning",
      shortName: "Learn",
      icon: <FiAward />,
      color: "#8B5CF6",
      gradient: "linear-gradient(135deg, #8B5CF6, #7C3AED)",
    },
    {
      id: "personal",
      name: "Personal",
      shortName: "Personal",
      icon: <FiStar />,
      color: "#F59E0B",
      gradient: "linear-gradient(135deg, #F59E0B, #D97706)",
    },
  ];

  const financialGoals = [
    {
      id: 1,
      name: "Emergency Fund",
      shortName: "Emergency",
      current: 8500,
      target: 15000,
      progress: 56.7,
      deadline: "Dec 2024",
      connectedAccount: "Savings Account",
      monthlyContribution: 500,
      category: "Savings",
      icon: "💰",
    },
    {
      id: 2,
      name: "New Car",
      shortName: "New Car",
      current: 12500,
      target: 30000,
      progress: 41.7,
      deadline: "Jun 2025",
      connectedAccount: "Car Fund",
      monthlyContribution: 750,
      category: "Major Purchase",
      icon: "🚗",
    },
    {
      id: 3,
      name: "Vacation to Japan",
      shortName: "Japan Trip",
      current: 2300,
      target: 5000,
      progress: 46,
      deadline: "Mar 2025",
      connectedAccount: "Travel Fund",
      monthlyContribution: 300,
      category: "Travel",
      icon: "✈️",
    },
  ];

  const features = [
    {
      id: 1,
      icon: <FiDollarSign />,
      title: "Financial Integration",
      shortTitle: "Finance Integration",
      description:
        "Seamlessly connect with your finances to track progress in real-time. Automatic updates as you save or spend.",
      shortDescription: "Connect finances & track progress automatically.",
      highlight: "Direct bank connection & sync",
      color: "#10B981",
    },
    {
      id: 2,
      icon: <FiPieChart />,
      title: "Smart Allocation",
      shortTitle: "Smart Allocation",
      description:
        "AI-powered suggestions for optimal fund allocation across multiple goals based on your income and timeline.",
      shortDescription: "AI-powered fund allocation across all goals.",
      highlight: "30% faster goal achievement",
      color: "#8B5CF6",
    },
    {
      id: 3,
      icon: <FiCalendar />,
      title: "Time-Bound Tracking",
      shortTitle: "Time Tracking",
      description:
        "Set deadlines and get daily/weekly progress updates. Adjust timelines dynamically as priorities change.",
      shortDescription: "Set deadlines with automatic progress updates.",
      highlight: "95% deadline accuracy rate",
      color: "#3B82F6",
    },
  ];

  useEffect(() => {
    const sequence = async () => {
      await progressControls.start({
        scaleX: [0, 0.567, 0.567],
        transition: {
          duration: 1.5,
          ease: "easeInOut",
        },
      });

      await financeProgressControls.start({
        width: ["0%", "85%"],
        transition: {
          duration: 2,
          ease: "easeInOut",
        },
      });
    };

    sequence();

    const interval = setInterval(() => {
      setActiveGoal((prev) => (prev + 1) % financialGoals.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [progressControls, financeProgressControls]);

  const handleFeatureClick = (index) => {
    setActiveGoal(index);
  };

  const calculateDaysRemaining = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - now;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <ShowcaseContainer>
      {/* Animated Background Elements */}
      <BackgroundCircles />
      <FloatingShapes />

      <ContentWrapper>
        <HeaderSection>
          <Badge>
            <FiTarget />
            <span>INTELLIGENT GOAL TRACKING</span>
          </Badge>
          <MainTitle>
            Achieve Any
            <GradientText>Goal</GradientText>
            with Precision
          </MainTitle>
          <Subtitle>
            From financial milestones to personal achievements, track every goal
            with smart insights, automated progress tracking, and seamless
            financial integration.
          </Subtitle>
        </HeaderSection>

        {/* Goal Type Selector - Optimized for mobile */}
        <GoalTypeSelector>
          {goalTypes.map((type) => (
            <GoalTypeButton
              key={type.id}
              active={goalType === type.id}
              onClick={() => setGoalType(type.id)}
              gradient={type.gradient}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              $isSmallScreen={isSmallScreen}
            >
              <GoalTypeIcon active={goalType === type.id}>
                {type.icon}
              </GoalTypeIcon>
              <GoalTypeName $isSmallScreen={isSmallScreen}>
                {isSmallScreen ? type.shortName : type.name}
              </GoalTypeName>
            </GoalTypeButton>
          ))}
        </GoalTypeSelector>

        <ShowcaseGrid>
          {/* Features Navigation - Stacked on mobile */}
          <FeatureNav>
            {features.map((feature, index) => (
              <FeatureNavItem
                key={feature.id}
                active={activeGoal === index}
                onClick={() => handleFeatureClick(index)}
                color={feature.color}
                whileHover={{ x: isSmallScreen ? 0 : 5 }}
                $isSmallScreen={isSmallScreen}
              >
                <NavIcon active={activeGoal === index} color={feature.color}>
                  {feature.icon}
                </NavIcon>
                <NavContent>
                  <NavTitle $isSmallScreen={isSmallScreen}>
                    {isSmallScreen ? feature.shortTitle : feature.title}
                  </NavTitle>
                  {!isSmallScreen && (
                    <NavHighlight>{feature.highlight}</NavHighlight>
                  )}
                </NavContent>
                {activeGoal === index && !isSmallScreen && (
                  <ActiveIndicator
                    layoutId="activeIndicator"
                    color={feature.color}
                  />
                )}
              </FeatureNavItem>
            ))}
          </FeatureNav>

          {/* Main Goal Display - Stacked on mobile */}
          <GoalDisplay>
            <GoalVisual>
              {/* Financial Goal Progress Visualization */}
              <FinanceGoalCard $isSmallScreen={isSmallScreen}>
                <GoalHeader $isSmallScreen={isSmallScreen}>
                  <GoalIcon $isSmallScreen={isSmallScreen}>
                    {financialGoals[activeGoal].icon}
                  </GoalIcon>
                  <GoalInfo>
                    <GoalName $isSmallScreen={isSmallScreen}>
                      {isSmallScreen
                        ? financialGoals[activeGoal].shortName
                        : financialGoals[activeGoal].name}
                    </GoalName>
                    <GoalCategory>
                      {financialGoals[activeGoal].category}
                    </GoalCategory>
                  </GoalInfo>
                  {!isSmallScreen && <GoalMenu>⋯</GoalMenu>}
                </GoalHeader>

                <FinancialProgress>
                  <AmountDisplay $isSmallScreen={isSmallScreen}>
                    <CurrentAmount $isSmallScreen={isSmallScreen}>
                      ${financialGoals[activeGoal].current.toLocaleString()}
                    </CurrentAmount>
                    <TargetAmount $isSmallScreen={isSmallScreen}>
                      ${financialGoals[activeGoal].target.toLocaleString()}
                    </TargetAmount>
                  </AmountDisplay>

                  <ProgressContainer>
                    <ProgressBar>
                      <ProgressFill
                        animate={progressControls}
                        gradient={goalTypes[activeGoal].gradient}
                      />
                    </ProgressBar>
                    <ProgressText $isSmallScreen={isSmallScreen}>
                      <ProgressPercent $isSmallScreen={isSmallScreen}>
                        {financialGoals[activeGoal].progress}% Complete
                      </ProgressPercent>
                      <RemainingTime $isSmallScreen={isSmallScreen}>
                        <FiClock />
                        {calculateDaysRemaining(
                          financialGoals[activeGoal].deadline
                        )}{" "}
                        days left
                      </RemainingTime>
                    </ProgressText>
                  </ProgressContainer>

                  {/* Financial Integration Demo */}
                  <FinanceIntegration $isSmallScreen={isSmallScreen}>
                    <IntegrationHeader>
                      <FiDollarSign />
                      <span>
                        {isSmallScreen ? "Connected:" : "Connected to:"}{" "}
                        {isSmallScreen
                          ? "Savings"
                          : financialGoals[activeGoal].connectedAccount}
                      </span>
                    </IntegrationHeader>
                    <MonthlyContribution>
                      <ContributionLabel $isSmallScreen={isSmallScreen}>
                        {isSmallScreen ? "Monthly" : "Monthly Contribution"}
                      </ContributionLabel>
                      <ContributionAmount $isSmallScreen={isSmallScreen}>
                        ${financialGoals[activeGoal].monthlyContribution}/mo
                      </ContributionAmount>
                    </MonthlyContribution>
                  </FinanceIntegration>
                </FinancialProgress>
              </FinanceGoalCard>

              {/* Connected Financial Dashboard Preview */}
              <FinanceDashboardPreview $isSmallScreen={isSmallScreen}>
                <DashboardHeader $isSmallScreen={isSmallScreen}>
                  <FiBarChart2 />
                  <span>
                    {isSmallScreen ? "Overview" : "Financial Overview"}
                  </span>
                </DashboardHeader>
                <DashboardMetrics $isSmallScreen={isSmallScreen}>
                  <Metric $isSmallScreen={isSmallScreen}>
                    <MetricLabel>Monthly Income</MetricLabel>
                    <MetricValue $isSmallScreen={isSmallScreen}>
                      $5,840
                    </MetricValue>
                    <MetricTrend positive>+12%</MetricTrend>
                  </Metric>
                  <Metric $isSmallScreen={isSmallScreen}>
                    <MetricLabel>Goal Allocation</MetricLabel>
                    <MetricValue $isSmallScreen={isSmallScreen}>
                      42%
                    </MetricValue>
                    <MetricBar>
                      <MetricFill
                        animate={financeProgressControls}
                        gradient={goalTypes[activeGoal].gradient}
                      />
                    </MetricBar>
                  </Metric>
                  <Metric $isSmallScreen={isSmallScreen}>
                    <MetricLabel>
                      {isSmallScreen ? "Completion" : "Projected Completion"}
                    </MetricLabel>
                    <MetricValue $isSmallScreen={isSmallScreen}>
                      {financialGoals[activeGoal].deadline}
                    </MetricValue>
                    {!isSmallScreen && (
                      <MetricSubtext>95% confidence</MetricSubtext>
                    )}
                  </Metric>
                </DashboardMetrics>
              </FinanceDashboardPreview>

              {/* Floating Achievements - Hidden on very small screens */}
              {!isSmallScreen && (
                <FloatingAchievements>
                  <AchievementBadge>
                    <FiAward />
                    <span>On Track</span>
                  </AchievementBadge>
                  <AchievementBadge>
                    <FiTrendingUp />
                    <span>+15% Ahead</span>
                  </AchievementBadge>
                </FloatingAchievements>
              )}
            </GoalVisual>

            <FeatureContent>
              <FeatureBadge gradient={features[activeGoal].color}>
                {features[activeGoal].icon}
                <span>{isSmallScreen ? "FEATURE" : "SMART FEATURE"}</span>
              </FeatureBadge>

              <FeatureTitle $isSmallScreen={isSmallScreen}>
                {isSmallScreen
                  ? features[activeGoal].shortTitle
                  : features[activeGoal].title}
              </FeatureTitle>

              <FeatureDescription $isSmallScreen={isSmallScreen}>
                {isSmallScreen
                  ? features[activeGoal].shortDescription
                  : features[activeGoal].description}
              </FeatureDescription>

              <GoalStats $isSmallScreen={isSmallScreen}>
                <Stat $isSmallScreen={isSmallScreen}>
                  <StatNumber $isSmallScreen={isSmallScreen}>98%</StatNumber>
                  <StatLabel>Accuracy</StatLabel>
                </Stat>
                <Stat $isSmallScreen={isSmallScreen}>
                  <StatNumber $isSmallScreen={isSmallScreen}>30%</StatNumber>
                  <StatLabel>Faster</StatLabel>
                </Stat>
                <Stat $isSmallScreen={isSmallScreen}>
                  <StatNumber $isSmallScreen={isSmallScreen}>24/7</StatNumber>
                  <StatLabel>Tracking</StatLabel>
                </Stat>
              </GoalStats>

              <FeatureHighlights>
                <HighlightItem $isSmallScreen={isSmallScreen}>
                  <CheckIcon>
                    <FiCheckCircle />
                  </CheckIcon>
                  <span>
                    {isSmallScreen
                      ? "Real-time finance integration"
                      : "Real-time financial integration"}
                  </span>
                </HighlightItem>
                <HighlightItem $isSmallScreen={isSmallScreen}>
                  <CheckIcon>
                    <FiCheckCircle />
                  </CheckIcon>
                  <span>
                    {isSmallScreen
                      ? "Auto progress calculation"
                      : "Automated progress calculation"}
                  </span>
                </HighlightItem>
                <HighlightItem $isSmallScreen={isSmallScreen}>
                  <CheckIcon>
                    <FiCheckCircle />
                  </CheckIcon>
                  <span>
                    {isSmallScreen
                      ? "Predictive completion dates"
                      : "Predictive completion dates"}
                  </span>
                </HighlightItem>
                <HighlightItem $isSmallScreen={isSmallScreen}>
                  <CheckIcon>
                    <FiCheckCircle />
                  </CheckIcon>
                  <span>
                    {isSmallScreen
                      ? "Multi-goal optimization"
                      : "Multi-goal optimization"}
                  </span>
                </HighlightItem>
              </FeatureHighlights>

              <CTASection>
                <CTAButtonGroup $isSmallScreen={isSmallScreen}>
                  <PrimaryCTA
                    gradient={features[activeGoal].color}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    $isSmallScreen={isSmallScreen}
                  >
                    <span>
                      {isSmallScreen ? "Start Now" : "Start Tracking Goals"}
                    </span>
                    <FiChevronRight />
                  </PrimaryCTA>
                </CTAButtonGroup>
              </CTASection>
            </FeatureContent>
          </GoalDisplay>
        </ShowcaseGrid>
      </ContentWrapper>
    </ShowcaseContainer>
  );
};

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(5deg); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components with enhanced mobile responsiveness
const ShowcaseContainer = styled.section`
  padding: 8rem 8%;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4rem 5%;
  }

  @media (max-width: 430px) {
    padding: 3rem 4%;
    min-height: auto;
  }

  @media (max-width: 375px) {
    padding: 2.5rem 3%;
  }
`;

const BackgroundCircles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;

  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    animation: ${float} 20s ease-in-out infinite;
  }

  &::before {
    width: 500px;
    height: 500px;
    background: radial-gradient(
      circle,
      rgba(146, 129, 221, 0.05) 0%,
      transparent 70%
    );
    top: -100px;
    right: -100px;
    animation-delay: 0s;

    @media (max-width: 768px) {
      width: 300px;
      height: 300px;
      top: -50px;
      right: -50px;
    }

    @media (max-width: 430px) {
      width: 200px;
      height: 200px;
      top: -30px;
      right: -30px;
    }
  }

  &::after {
    width: 400px;
    height: 400px;
    background: radial-gradient(
      circle,
      rgba(16, 185, 129, 0.05) 0%,
      transparent 70%
    );
    bottom: -50px;
    left: -50px;
    animation-delay: 10s;

    @media (max-width: 768px) {
      width: 250px;
      height: 250px;
      bottom: -30px;
      left: -30px;
    }

    @media (max-width: 430px) {
      width: 150px;
      height: 150px;
      bottom: -20px;
      left: -20px;
    }
  }
`;

const FloatingShapes = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;

  &::before,
  &::after {
    content: "🎯";
    position: absolute;
    font-size: 2rem;
    opacity: 0.1;
    animation: ${float} 15s ease-in-out infinite;

    @media (max-width: 430px) {
      font-size: 1.5rem;
    }
  }

  &::before {
    top: 20%;
    left: 10%;
    animation-delay: 0s;

    @media (max-width: 430px) {
      top: 15%;
      left: 5%;
    }
  }

  &::after {
    bottom: 30%;
    right: 15%;
    content: "💰";
    animation-delay: 5s;

    @media (max-width: 430px) {
      bottom: 25%;
      right: 10%;
    }
  }
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 1;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }

  @media (max-width: 430px) {
    margin-bottom: 2rem;
  }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.5rem;
  background: rgba(146, 129, 221, 0.1);
  border: 1px solid rgba(146, 129, 221, 0.3);
  border-radius: 100px;
  color: #9281dd;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 1px;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 0.5rem 1.25rem;
    font-size: 0.8125rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 430px) {
    padding: 0.375rem 1rem;
    font-size: 0.75rem;
    margin-bottom: 1.25rem;
    gap: 0.375rem;

    span {
      font-size: 0.6875rem;
    }
  }

  @media (max-width: 375px) {
    padding: 0.375rem 0.875rem;
  }
`;

const MainTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 300;
  line-height: 1.2;
  color: #1a1a2e;
  margin-bottom: 1.5rem;
  font-family: "Montserrat", sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 430px) {
    font-size: 2rem;
    margin-bottom: 1rem;
    line-height: 1.1;
  }

  @media (max-width: 375px) {
    font-size: 1.75rem;
  }
`;

const GradientText = styled.span`
  color: #9281dd;
  margin: 0 8px;
  font-weight: 400;
  font-size: 4rem;

  @media (max-width: 768px) {
    font-size: 3rem;
    margin: 0 4px;
  }

  @media (max-width: 430px) {
    font-size: 2.25rem;
    margin: 0 3px;
  }

  @media (max-width: 375px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: #4a5568;
  max-width: 700px;
  margin: 0 auto;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1.125rem;
    padding: 0 1rem;
    line-height: 1.5;
  }

  @media (max-width: 430px) {
    font-size: 1rem;
    padding: 0 0.5rem;
    line-height: 1.4;
  }

  @media (max-width: 375px) {
    font-size: 0.9375rem;
  }
`;

const GoalTypeSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 4rem;
  flex-wrap: wrap;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;

  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 3rem;
  }

  @media (max-width: 430px) {
    gap: 0.375rem;
    margin-bottom: 2rem;
  }
`;

const GoalTypeButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: ${({ active }) => (active ? "white" : "transparent")};
  border: 1px solid
    ${({ active, gradient }) =>
      active ? "rgba(146, 129, 221, 0.3)" : "rgba(0, 0, 0, 0.1)"};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${({ active }) =>
    active ? "0 10px 30px rgba(146, 129, 221, 0.1)" : "none"};

  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(146, 129, 221, 0.1);
  }

  ${({ active, gradient }) =>
    active &&
    css`
      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: ${gradient};
        border-radius: 12px 12px 0 0;
      }
    `}

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    gap: 0.5rem;
    border-radius: 10px;
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.5rem 0.75rem" : "0.75rem 1rem"};
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.375rem" : "0.5rem")};
    border-radius: 8px;
    min-width: ${({ $isSmallScreen }) => ($isSmallScreen ? "auto" : "100px")};
    flex: ${({ $isSmallScreen }) => ($isSmallScreen ? "1 0 auto" : "none")};
  }

  @media (max-width: 375px) {
    padding: 0.5rem 0.625rem;
    gap: 0.25rem;
  }
`;

const GoalTypeIcon = styled.div`
  font-size: 1.25rem;
  color: ${({ active }) => (active ? "#9281dd" : "#718096")};

  @media (max-width: 430px) {
    font-size: 1.125rem;
  }

  @media (max-width: 375px) {
    font-size: 1rem;
  }
`;

const GoalTypeName = styled.span`
  font-weight: 600;
  color: #1a1a2e;
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.8125rem" : "0.9375rem"};
  white-space: nowrap;

  @media (max-width: 375px) {
    font-size: 0.75rem;
  }
`;

const ShowcaseGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 4rem;
  margin-bottom: 6rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  @media (max-width: 430px) {
    gap: 2rem;
    margin-bottom: 4rem;
  }

  @media (max-width: 375px) {
    gap: 1.5rem;
    margin-bottom: 3rem;
  }
`;

const FeatureNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 1024px) {
    order: 2;
  }

  @media (max-width: 430px) {
    gap: 0.75rem;
  }

  @media (max-width: 375px) {
    gap: 0.5rem;
  }
`;

const FeatureNavItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: ${({ active }) => (active ? "#ffffff" : "transparent")};
  border: 1px solid
    ${({ active, color }) => (active ? `${color}40` : "rgba(0, 0, 0, 0.1)")};
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: ${({ active }) =>
    active ? "0 10px 40px rgba(146, 129, 221, 0.1)" : "none"};

  &:hover {
    background: #ffffff;
    transform: translateX(
      ${({ $isSmallScreen }) => ($isSmallScreen ? "0" : "5px")}
    );
    box-shadow: 0 10px 30px rgba(146, 129, 221, 0.1);
    border-color: rgba(146, 129, 221, 0.3);
  }

  ${({ active, color }) =>
    active &&
    css`
      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: ${color};
      }
    `}

  @media (max-width: 768px) {
    padding: 1rem;
    gap: 0.75rem;
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.875rem" : "1rem")};
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.625rem" : "0.75rem")};
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "12px" : "16px"};
  }

  @media (max-width: 375px) {
    padding: 0.75rem;
    gap: 0.5rem;
    border-radius: 10px;
  }
`;

const NavIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  background: ${({ active, color }) =>
    active ? color : "rgba(146, 129, 221, 0.1)"};
  color: ${({ active }) => (active ? "#ffffff" : "#9281dd")};
  transition: all 0.3s ease;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  @media (max-width: 430px) {
    width: 36px;
    height: 36px;
    font-size: 1.125rem;
    border-radius: 10px;
  }

  @media (max-width: 375px) {
    width: 32px;
    height: 32px;
    font-size: 1rem;
    border-radius: 8px;
  }
`;

const NavContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NavTitle = styled.div`
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: ${({ $isSmallScreen }) => ($isSmallScreen ? "0" : "0.25rem")};
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.9375rem" : "1.125rem"};
  line-height: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.2" : "1.4")};

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 430px) {
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.875rem" : "0.9375rem"};
  }

  @media (max-width: 375px) {
    font-size: 0.8125rem;
  }
`;

const NavHighlight = styled.div`
  font-size: 0.875rem;
  color: #718096;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ActiveIndicator = styled(motion.div)`
  position: absolute;
  right: 1.25rem;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ color }) => color};

  @media (max-width: 768px) {
    display: none;
  }
`;

const GoalDisplay = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  @media (max-width: 430px) {
    gap: 2rem;
  }

  @media (max-width: 375px) {
    gap: 1.5rem;
  }
`;

const GoalVisual = styled.div`
  position: relative;

  @media (max-width: 768px) {
    margin: 0 -1rem;
  }

  @media (max-width: 430px) {
    margin: 0 -0.75rem;
  }
`;

const FinanceGoalCard = styled.div`
  background: #ffffff;
  border: 1px solid rgba(146, 129, 221, 0.2);
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 20px 60px rgba(146, 129, 221, 0.1),
    0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 2rem;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 30px 80px rgba(146, 129, 221, 0.15),
      0 15px 40px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
    margin: 0 1rem 1.5rem;
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.25rem" : "1.5rem")};
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "14px" : "16px"};
    margin: 0 0.75rem 1.25rem;
  }

  @media (max-width: 375px) {
    padding: 1rem;
    border-radius: 12px;
    margin: 0 0.5rem 1rem;
  }
`;

const GoalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.75rem" : "1rem")};
  margin-bottom: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "1.5rem" : "2rem"};

  @media (max-width: 375px) {
    gap: 0.625rem;
    margin-bottom: 1.25rem;
  }
`;

const GoalIcon = styled.div`
  font-size: 2rem;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(146, 129, 221, 0.1);
  border-radius: 16px;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }

  @media (max-width: 430px) {
    width: ${({ $isSmallScreen }) => ($isSmallScreen ? "44px" : "50px")};
    height: ${({ $isSmallScreen }) => ($isSmallScreen ? "44px" : "50px")};
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "1.25rem" : "1.5rem"};
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "12px" : "16px"};
  }

  @media (max-width: 375px) {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
    border-radius: 10px;
  }
`;

const GoalInfo = styled.div`
  flex: 1;
`;

const GoalName = styled.h3`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.25rem" : "1.5rem")};
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 0.25rem 0;
  line-height: 1.2;

  @media (max-width: 375px) {
    font-size: 1.125rem;
    margin-bottom: 0.125rem;
  }
`;

const GoalCategory = styled.div`
  font-size: 0.875rem;
  color: #718096;
  background: rgba(146, 129, 221, 0.1);
  padding: 0.25rem 0.75rem;
  border-radius: 100px;
  display: inline-block;

  @media (max-width: 430px) {
    font-size: 0.8125rem;
    padding: 0.1875rem 0.5rem;
  }

  @media (max-width: 375px) {
    font-size: 0.75rem;
    padding: 0.125rem 0.375rem;
  }
`;

const GoalMenu = styled.button`
  background: none;
  border: none;
  color: #718096;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;

  &:hover {
    color: #1a1a2e;
  }
`;

const FinancialProgress = styled.div``;

const AmountDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "1rem" : "1.5rem"};
  flex-wrap: wrap;
  gap: 0.5rem;

  @media (max-width: 375px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const CurrentAmount = styled.div`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "2rem" : "2.5rem")};
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1;

  @media (max-width: 375px) {
    font-size: 1.75rem;
  }
`;

const TargetAmount = styled.div`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "1rem" : "1.25rem")};
  color: #718096;
  position: relative;

  &::before {
    content: "→";
    margin-right: 0.5rem;
    color: #9281dd;
  }

  @media (max-width: 375px) {
    font-size: 0.9375rem;
  }
`;

const ProgressContainer = styled.div`
  margin: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.5rem 0" : "2rem 0")};

  @media (max-width: 375px) {
    margin: 1.25rem 0;
  }
`;

const ProgressBar = styled.div`
  height: 12px;
  background: rgba(146, 129, 221, 0.1);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 1rem;

  @media (max-width: 430px) {
    height: 10px;
  }

  @media (max-width: 375px) {
    height: 8px;
    margin-bottom: 0.75rem;
  }
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  transform-origin: left;
  background: ${({ gradient }) =>
    gradient || "linear-gradient(90deg, #10B981, #9281dd)"};
  border-radius: 6px;
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: ${({ $isSmallScreen }) => ($isSmallScreen ? "wrap" : "nowrap")};
  gap: 0.5rem;

  @media (max-width: 375px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const ProgressPercent = styled.div`
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "1.125rem" : "1.25rem"};
  font-weight: 700;
  color: #1a1a2e;
  white-space: nowrap;

  @media (max-width: 375px) {
    font-size: 1rem;
  }
`;

const RemainingTime = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.8125rem" : "0.875rem"};
  color: #718096;
  white-space: nowrap;

  @media (max-width: 375px) {
    font-size: 0.75rem;
    gap: 0.375rem;
  }
`;

const FinanceIntegration = styled.div`
  background: rgba(146, 129, 221, 0.05);
  border: 1px solid rgba(146, 129, 221, 0.1);
  border-radius: 16px;
  padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.25rem" : "1.5rem")};
  margin-top: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.5rem" : "2rem")};

  @media (max-width: 430px) {
    border-radius: 14px;
  }

  @media (max-width: 375px) {
    padding: 1rem;
    border-radius: 12px;
    margin-top: 1.25rem;
  }
`;

const IntegrationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #9281dd;
  font-weight: 600;
  margin-bottom: 1rem;
  font-size: 0.9375rem;

  @media (max-width: 430px) {
    font-size: 0.875rem;
    gap: 0.5rem;
  }

  @media (max-width: 375px) {
    font-size: 0.8125rem;
    margin-bottom: 0.75rem;
  }
`;

const MonthlyContribution = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(146, 129, 221, 0.2);

  @media (max-width: 430px) {
    padding: 0.875rem;
    border-radius: 10px;
  }

  @media (max-width: 375px) {
    padding: 0.75rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const ContributionLabel = styled.div`
  color: #718096;
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "0.8125rem" : "0.875rem"};

  @media (max-width: 375px) {
    font-size: 0.75rem;
  }
`;

const ContributionAmount = styled.div`
  font-size: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "1.125rem" : "1.25rem"};
  font-weight: 700;
  color: #10b981;

  @media (max-width: 375px) {
    font-size: 1rem;
  }
`;

const FinanceDashboardPreview = styled.div`
  background: #ffffff;
  border: 1px solid rgba(146, 129, 221, 0.2);
  border-radius: 24px;
  padding: 1.5rem;
  box-shadow: 0 20px 60px rgba(146, 129, 221, 0.1),
    0 10px 30px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 16px;
    margin: 0 1rem;
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "1rem" : "1.25rem")};
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "14px" : "16px"};
    margin: 0 0.75rem;
  }

  @media (max-width: 375px) {
    padding: 0.875rem;
    border-radius: 12px;
    margin: 0 0.5rem;
  }
`;

const DashboardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #9281dd;
  font-weight: 600;
  margin-bottom: 1.5rem;
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.9375rem" : "1rem")};

  @media (max-width: 375px) {
    margin-bottom: 1rem;
    font-size: 0.875rem;
    gap: 0.5rem;
  }
`;

const DashboardMetrics = styled.div`
  display: grid;
  grid-template-columns: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "1fr" : "repeat(3, 1fr)"};
  gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "1rem" : "1.5rem")};

  @media (max-width: 375px) {
    gap: 0.75rem;
  }
`;

const Metric = styled.div`
  padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.875rem" : "1rem")};
  background: rgba(146, 129, 221, 0.05);
  border-radius: 12px;

  @media (max-width: 430px) {
    border-radius: 10px;
  }

  @media (max-width: 375px) {
    padding: 0.75rem;
    border-radius: 8px;
  }
`;

const MetricLabel = styled.div`
  font-size: 0.75rem;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0.5rem;

  @media (max-width: 375px) {
    font-size: 0.6875rem;
    margin-bottom: 0.375rem;
  }
`;

const MetricValue = styled.div`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.25rem" : "1.5rem")};
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 0.5rem;

  @media (max-width: 375px) {
    font-size: 1.125rem;
    margin-bottom: 0.25rem;
  }
`;

const MetricTrend = styled.div`
  font-size: 0.875rem;
  color: ${({ positive }) => (positive ? "#10B981" : "#EF4444")};
  font-weight: 600;

  @media (max-width: 375px) {
    font-size: 0.8125rem;
  }
`;

const MetricBar = styled.div`
  height: 6px;
  background: rgba(146, 129, 221, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 0.5rem;

  @media (max-width: 375px) {
    height: 4px;
    margin-top: 0.375rem;
  }
`;

const MetricFill = styled(motion.div)`
  height: 100%;
  background: ${({ gradient }) =>
    gradient || "linear-gradient(90deg, #10B981, #9281dd)"};
  border-radius: 3px;
`;

const MetricSubtext = styled.div`
  font-size: 0.75rem;
  color: #718096;
  margin-top: 0.25rem;

  @media (max-width: 375px) {
    font-size: 0.6875rem;
  }
`;

const FloatingAchievements = styled.div`
  position: absolute;
  top: -15px;
  right: 20px;
  display: flex;
  gap: 0.75rem;
  z-index: 2;

  @media (max-width: 768px) {
    position: relative;
    top: 0;
    right: 0;
    margin-top: 1rem;
    justify-content: center;
  }
`;

const AchievementBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  border-radius: 100px;
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 5px 15px rgba(245, 158, 11, 0.3);
  animation: ${float} 3s ease-in-out infinite;
  animation-delay: ${() => Math.random() * 2}s;

  &:nth-child(2) {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    animation-delay: ${() => Math.random() * 2}s;
  }
`;

const FeatureContent = styled.div`
  animation: ${fadeInUp} 0.6s ease-out;

  @media (max-width: 1024px) {
    order: -1;
  }
`;

const FeatureBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  background: ${({ gradient }) =>
    gradient ? gradient : "linear-gradient(135deg, #9281dd, #6d5bd4)"};
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 1px;
  border-radius: 100px;
  margin-bottom: 1.5rem;

  @media (max-width: 430px) {
    padding: 0.375rem 1rem;
    font-size: 0.8125rem;
    gap: 0.375rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 375px) {
    padding: 0.375rem 0.875rem;
    font-size: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const FeatureTitle = styled.h2`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "2rem" : "2.5rem")};
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 430px) {
    margin-bottom: 1.25rem;
  }

  @media (max-width: 375px) {
    font-size: 1.75rem;
    margin-bottom: 1rem;
  }
`;

const FeatureDescription = styled.p`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "1rem" : "1.125rem")};
  line-height: 1.7;
  color: #4a5568;
  margin-bottom: 2.5rem;
  opacity: 0.9;

  @media (max-width: 430px) {
    font-size: 0.9375rem;
    line-height: 1.6;
    margin-bottom: 2rem;
  }

  @media (max-width: 375px) {
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 1.5rem;
  }
`;

const GoalStats = styled.div`
  display: grid;
  grid-template-columns: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "repeat(3, 1fr)" : "repeat(3, 1fr)"};
  gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.75rem" : "1rem")};
  margin-bottom: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "2rem" : "2.5rem"};

  @media (max-width: 375px) {
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
`;

const Stat = styled.div`
  text-align: center;
  padding: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.875rem" : "1rem")};
  background: rgba(146, 129, 221, 0.05);
  border-radius: 12px;

  @media (max-width: 430px) {
    border-radius: 10px;
  }

  @media (max-width: 375px) {
    padding: 0.75rem;
    border-radius: 8px;
  }
`;

const StatNumber = styled.div`
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.25rem" : "1.5rem")};
  font-weight: 700;
  color: #9281dd;
  margin-bottom: 0.25rem;

  @media (max-width: 375px) {
    font-size: 1.125rem;
    margin-bottom: 0.125rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 375px) {
    font-size: 0.6875rem;
  }
`;

const FeatureHighlights = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.75rem" : "1rem")};
  margin-bottom: ${({ $isSmallScreen }) => ($isSmallScreen ? "2rem" : "3rem")};

  @media (max-width: 375px) {
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
`;

const HighlightItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  color: #1a1a2e;
  font-weight: 500;

  span {
    flex: 1;
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.9375rem" : "1rem"};
    line-height: ${({ $isSmallScreen }) => ($isSmallScreen ? "1.4" : "1.5")};

    @media (max-width: 375px) {
      font-size: 0.875rem;
      line-height: 1.3;
    }
  }

  @media (max-width: 430px) {
    gap: 0.75rem;
  }

  @media (max-width: 375px) {
    gap: 0.625rem;
    align-items: center;
  }
`;

const CheckIcon = styled.div`
  color: #10b981;
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.125rem" : "0")};

  @media (max-width: 430px) {
    font-size: 1.125rem;
  }

  @media (max-width: 375px) {
    font-size: 1rem;
    margin-top: 0;
  }
`;

const CTASection = styled.div`
  padding-top: 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  @media (max-width: 430px) {
    padding-top: 1.5rem;
  }

  @media (max-width: 375px) {
    padding-top: 1.25rem;
  }
`;

const CTAButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }

  @media (max-width: 430px) {
    gap: ${({ $isSmallScreen }) => ($isSmallScreen ? "0.5rem" : "0.75rem")};
  }
`;

const PrimaryCTA = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: ${({ $isSmallScreen }) =>
    $isSmallScreen ? "1rem 2rem" : "1.25rem 2.5rem"};
  background: ${({ gradient }) =>
    gradient ? gradient : "linear-gradient(135deg, #9281dd, #6d5bd4)"};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? "1rem" : "1.125rem")};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(146, 129, 221, 0.3);

  &:hover {
    box-shadow: 0 20px 40px rgba(146, 129, 221, 0.4);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }

  @media (max-width: 430px) {
    padding: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.875rem 1.5rem" : "1rem 2rem"};
    font-size: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "0.9375rem" : "1rem"};
    border-radius: ${({ $isSmallScreen }) =>
      $isSmallScreen ? "10px" : "12px"};
  }

  @media (max-width: 375px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
    border-radius: 8px;
    gap: 0.5rem;
  }
`;

export default GoalShawcase;
